export type GuideCta = {
  label: string;
  to: string;
};

export type GuideStep = {
  id: string;
  label: string;
};

export type GuideSection = {
  id: string;
  title: string;
  summary: string;
  paragraphs?: string[];
  staffDo: string[];
  tip?: string;
  cta?: GuideCta;
  secondaryCta?: GuideCta;
};

export const GUIDE_STEPS: GuideStep[] = [
  { id: "before-you-start", label: "Before you start" },
  { id: "register", label: "Register" },
  { id: "verification", label: "Verify" },
  { id: "approve", label: "Approve" },
  { id: "cards", label: "Cards" },
  { id: "notifications", label: "Notify" },
  { id: "manage", label: "Manage" },
  { id: "policies", label: "Policies" },
  { id: "problems", label: "Problems" },
];

export const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: "before-you-start",
    title: "1. Before you start",
    summary:
      "Patron management works smoothly when your library’s rules and registration form are set up first. Do this once (or whenever policies change).",
    paragraphs: [
      "Patron types control who can register (for example Student or Faculty), borrowing limits, and which fields or documents are required.",
      "Registration setup controls whether people can self-register online, whether a fee is charged, and what happens after approval.",
    ],
    staffDo: [
      "Confirm patron types exist and match how your library groups members.",
      "Review registration settings: required fields, document uploads, and payment rules.",
      "Make sure staff who will approve applications have permission to do so.",
    ],
    tip: "If the registration page looks empty or rejects everyone, start with patron types and registration setup, not the approval queue.",
    cta: { label: "Open patron types", to: "/setup/patron-types" },
    secondaryCta: { label: "Registration setup", to: "/setup/registration" },
  },
  {
    id: "register",
    title: "2. How patrons register",
    summary:
      "Patrons join from your library’s public registration page (usually linked from your tenant site). They choose a patron type, enter contact details, and upload any documents you require.",
    paragraphs: [
      "Submitted files are stored securely for staff review. Patrons see a confirmation that their application was received.",
      "Until staff approve the application (and any required payment is complete), the person cannot borrow materials as a full member.",
    ],
    staffDo: [
      "Share the correct registration link with new members (or your website).",
      "Tell applicants which documents you need (student ID, letter, etc.) before they start.",
      "Watch Registration Approval for new applications instead of asking people to email forms.",
    ],
    tip: "Encourage applicants to use an email they check often; approval and card updates go there.",
    cta: { label: "Open registration approval", to: "/admin/registrations" },
  },
  {
    id: "verification",
    title: "3. Verification (email and documents)",
    summary: "Two different checks often happen before someone becomes an active patron.",
    paragraphs: [
      "Email verification confirms the address belongs to the applicant. This is usually done when they click a link in their email.",
      "Document review is done by library staff. PhiloLMS does not automatically “approve” ID photos or PDFs; you read them and decide.",
      "When approval rules and payment (if configured) are satisfied, the system can issue a virtual library card and start the physical card workflow.",
    ],
    staffDo: [
      "Open each pending application and review uploaded documents carefully.",
      "If something is missing or unreadable, reject with a clear reason so the patron can try again.",
      "Do not approve accounts that have not completed email verification if your library requires it.",
    ],
    tip: "Rejection reasons should be polite and specific (for example “Student ID photo is blurry. Please upload a clearer image”).",
    cta: { label: "Review pending applications", to: "/admin/registrations" },
  },
  {
    id: "approve",
    title: "4. Review and approve registrations",
    summary:
      "Registration Approval is the staff inbox for new members. Approve when the person is eligible; reject when they are not, or when documents are incomplete.",
    paragraphs: [
      "You can search by name, email, or phone, open supporting documents, and act on one application or several at once when your permissions allow.",
      "After a decision, patrons receive email and in-app updates so they know what happened next.",
    ],
    staffDo: [
      "Work the pending queue regularly so applicants are not left waiting.",
      "Use bulk actions only when you have reviewed each selected application.",
      "After approval, confirm whether a fee or card step is still outstanding.",
    ],
    tip: "Approved does not always mean “ready to borrow.” Check card and payment status if your library charges a registration fee.",
    cta: { label: "Go to registration approval", to: "/admin/registrations" },
  },
  {
    id: "cards",
    title: "5. Library cards (virtual and physical)",
    summary:
      "Most patrons get a virtual card (for example a QR code in the app) as soon as they are eligible. Physical cards are optional and move through a processing queue.",
    paragraphs: [
      "Card Processing tracks physical cards from generation through printing, ready-for-pickup, and collected.",
      "Staff can print batches, mark cards ready, and record pickup. Deadlines and expiry checks help you clear old uncollected cards.",
    ],
    staffDo: [
      "Open Card Processing when new cards appear in the queue.",
      "Print cards in batches when possible, then mark them ready for pickup.",
      "When a patron collects their card, mark it picked up so the queue stays accurate.",
      "Run expiry / overdue pickup checks periodically if your library sets pickup deadlines.",
    ],
    tip: "Virtual cards let members use the library while they wait for a printed card, unless your policy requires the physical card first.",
    cta: { label: "Open card processing", to: "/admin/cards" },
  },
  {
    id: "notifications",
    title: "6. Notifications",
    summary:
      "PhiloLMS keeps patrons and staff informed without relying on paper notes at the desk.",
    paragraphs: [
      "Patrons typically receive both email and in-app messages for important events: registration decisions, card ready for pickup, and pickup reminders.",
      "Staff who process physical cards get in-app notifications when new cards need attention. Email for that queue is usually off by default so inboxes stay quiet.",
      "Anyone can open Notifications from the app menu to read items and mark them as read.",
    ],
    staffDo: [
      "Check Notifications when starting a shift for card or registration alerts.",
      "Remind patrons to check email and the in-app bell if they say they “never heard back.”",
    ],
    tip: "If a patron missed an approval email, the decision still appears under Notifications when they sign in.",
    cta: { label: "Open notifications", to: "/notifications" },
  },
  {
    id: "manage",
    title: "7. Managing patron accounts",
    summary:
      "The Patrons list is your directory of members. Open a person to update details, change membership settings, or restrict borrowing when needed.",
    paragraphs: [
      "From a patron’s profile you can update name and phone, change patron type or home branch, suspend or reactivate borrowing access, and review audit history for that account.",
      "Send message delivers an in-app note to the patron. When your workflow allows, you can also send the same message by email so they see it outside the app.",
    ],
    staffDo: [
      "Search by name, email, or phone before creating a duplicate registration.",
      "Use suspend for temporary borrowing blocks; reactivate when the issue is resolved.",
      "Change patron type only when the person’s category truly changed (for example student → alumni).",
      "Use Send message for reminders that should stay on the account record.",
    ],
    tip: "Email addresses are usually changed by an administrator process, not by every desk staffer. Prefer phone updates for day-to-day corrections.",
    cta: { label: "Open patrons", to: "/patrons" },
  },
  {
    id: "policies",
    title: "8. Auto-renewal and circulation policies",
    summary:
      "Loan length, renewals, and fines come from your library’s circulation policies. Patron management connects those rules to each member.",
    paragraphs: [
      "Tenant-wide settings can allow loans to renew automatically when no one else is waiting for the item.",
      "You can override auto-renewal for one patron (on their membership) when that person should not get automatic renewals.",
      "Individual copies in the catalog can also be excluded from auto-renewal when a material must stay on a short loan or special collection.",
    ],
    staffDo: [
      "Set library-wide renewal and fine rules under circulation policies.",
      "Use per-patron auto-renewal overrides sparingly and document why in your internal notes if needed.",
      "Explain due dates and renewals to patrons using the same rules the system applies at checkout.",
    ],
    tip: "Confused about a due date? Check the patron’s type and the item’s loan rules before changing anything on the account.",
    cta: { label: "Circulation policies", to: "/setup/circulation-policies" },
    secondaryCta: { label: "Browse patrons", to: "/patrons" },
  },
  {
    id: "problems",
    title: "9. Common problems",
    summary: "Quick fixes for issues staff see most often at the desk.",
    paragraphs: [
      "“I registered but nothing happened”: Check Registration Approval for their application, then email verification and any unpaid registration fee.",
      "“I was approved but I still can’t borrow”: Confirm account is not suspended, card status is valid, and membership is in an active state.",
      "“My card isn’t ready”: Check Card Processing for print / ready / pickup status and whether a pickup deadline passed.",
      "“I didn’t get an email”: Ask them to check spam, then open Notifications after login; resend or re-explain the decision from Registration Approval if needed.",
      "“Wrong patron type”: Open the patron profile and update type or home branch; do not create a second registration.",
    ],
    staffDo: [
      "Reproduce the issue in PhiloLMS before asking the patron to re-register.",
      "Prefer fixing the existing account over creating duplicates.",
      "Escalate payment or setup problems to a tenant admin when fees or registration settings look wrong.",
    ],
    tip: "Duplicate accounts cause holds and fines to split across profiles. Always search Patrons first.",
    cta: { label: "Search patrons", to: "/patrons" },
    secondaryCta: { label: "Registration queue", to: "/admin/registrations" },
  },
];
